import { z } from 'zod';
import { compiledMacroPackageSchema } from '../macroPackage/compiledPackage';
import { rawAuthoringPackageManifestSchema } from '../macroPackage/rawAuthoringPackage';
import { colorIdentitySchema, macroColorDigestSchema, readyItpNormalizationSchema } from './schemas';
import { MACRO_COLOR_POLICY_V1 } from './colorPolicy';

const safe = z.number().int().nonnegative().safe();
const asset = z.object({ file: z.string().min(1).max(256), sha256: macroColorDigestSchema, bytes: safe,
  width: z.number().int().positive().max(16_384), height: z.number().int().positive().max(16_384) }).strict();
const colorClosure = z.object({ profile: z.literal(MACRO_COLOR_POLICY_V1.profile), recipeDigest: macroColorDigestSchema,
  identity: colorIdentitySchema, canonicalReferences: z.array(asset).max(16_384),
  previewRecipe: z.literal(MACRO_COLOR_POLICY_V1.previewRecipe), previewRecipeDigest: macroColorDigestSchema }).strict();

/** Declared raw-v5 parser. It does not widen the active raw-authoring parser. */
export const rawColorPackageV5Schema = z.unknown().transform((input, context) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Raw v5 object required' }); return z.NEVER;
  }
  const { color, ...candidate } = input as Record<string, unknown>;
  if (candidate['formatVersion'] !== 5) { context.addIssue({ code: z.ZodIssueCode.custom, message: 'Raw v5 required' }); return z.NEVER; }
  const base = rawAuthoringPackageManifestSchema.safeParse({ ...candidate, formatVersion: 4 });
  const parsedColor = colorClosure.safeParse(color);
  if (!base.success || !parsedColor.success) {
    for (const issue of [...(base.success ? [] : base.error.issues), ...(parsedColor.success ? [] : parsedColor.error.issues)]) context.addIssue(issue);
    return z.NEVER;
  }
  return { ...base.data, formatVersion: 5 as const, color: parsedColor.data };
});

/** Declared compiled-v3 parser. Runtime capability composition remains a separate gate. */
export const compiledColorPackageV3Schema = z.unknown().transform((input, context) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Compiled v3 object required' }); return z.NEVER;
  }
  const { color, ...candidate } = input as Record<string, unknown>;
  if (candidate['formatVersion'] !== 3) { context.addIssue({ code: z.ZodIssueCode.custom, message: 'Compiled v3 required' }); return z.NEVER; }
  const base = compiledMacroPackageSchema.safeParse({ ...candidate, formatVersion: 2, profile: 'playcast-visual256-v2' });
  const parsedColor = colorClosure.safeParse(color);
  if (!base.success || !parsedColor.success) {
    for (const issue of [...(base.success ? [] : base.error.issues), ...(parsedColor.success ? [] : parsedColor.error.issues)]) context.addIssue(issue);
    return z.NEVER;
  }
  return { ...base.data, formatVersion: 3 as const, profile: MACRO_COLOR_POLICY_V1.profile, color: parsedColor.data };
});

export const colorCalibrationEvidenceV3Schema = z.object({ schemaVersion: z.literal(3),
  profile: z.literal(MACRO_COLOR_POLICY_V1.profile), normalization: readyItpNormalizationSchema,
  requiredCasesDigest: macroColorDigestSchema, validationCasesDigest: macroColorDigestSchema,
  requiredPositiveCount: safe, acceptedRequiredPositiveCount: safe, negativeCount: safe,
  falseMatchCount: safe, missingCaseCount: safe }).strict().superRefine((value, context) => {
  if (value.requiredPositiveCount !== value.acceptedRequiredPositiveCount || value.falseMatchCount !== 0 || value.missingCaseCount !== 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Color validation coverage is incomplete' });
  }
});
